import { useEffect, useMemo, useRef, useState } from 'react'

import merchantApi from '../../services/merchantApi'

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts'

import {
  CaretDown,
  CheckCircle
} from '@phosphor-icons/react'

import './Dashboard.scss'
import Header from '../../components/Header/Header'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react/dist/iconify.js'

const DEFAULT_STATS = [
  {
    label: 'REVENUE',
    value: 'RM0.00',
    trend: null,
    positive: true,
  },
  {
    label: 'ORDERS',
    value: '0',
    trend: null,
    positive: true,
  },
  {
    label: 'AVG ORDER VALUE',
    value: 'RM0.00',
    trend: null,
    positive: true,
  },
  {
    label: 'DISCOUNTS GIVEN',
    value: 'RM0.00',
    meta: '0 vouchers • 0 promo codes',
  },
]

const DEFAULT_REVENUE_DATA = [
  { date: 'No data', revenue: 0 },
]

const getPayload = (response) => response?.data || response || {}

const asNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback
  const cleaned = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value
  const number = Number(cleaned)
  return Number.isFinite(number) ? number : fallback
}

const formatMoney = (value) => {
  const amount = asNumber(value)

  return `RM${amount.toLocaleString('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const formatNumber = (value) => asNumber(value).toLocaleString('en-MY')

const formatTrend = (value) => {
  if (value === null || value === undefined || value === '') return null
  const number = asNumber(value)
  const sign = number > 0 ? '+' : ''
  return `${sign}${number.toFixed(1)}%`
}

const pick = (source, keys, fallback = undefined) => {
  for (const key of keys) {
    if (source && source[key] !== undefined && source[key] !== null) {
      return source[key]
    }
  }

  return fallback
}

const normalizeDashboard = (response) => {
  const payload = getPayload(response)
  const summary = payload.summary || payload.stats || payload.overview || payload
  const revenue = pick(summary, ['revenue', 'total_revenue', 'sales', 'total_sales'], 0)
  const orders = pick(summary, ['orders', 'total_orders', 'order_count'], 0)
  const avgOrder = pick(summary, ['avg_order_value', 'average_order_value', 'aov'], 0)
  const discount = pick(summary, ['discounts_given', 'discount_amount', 'total_discount'], 0)
  const voucherCount = pick(summary, ['voucher_count', 'active_vouchers'], 0)
  const promoCount = pick(summary, ['promo_code_count', 'active_promo_codes'], 0)

  const stats = [
    {
      label: 'REVENUE',
      value: formatMoney(revenue),
      trend: formatTrend(pick(summary, ['revenue_trend', 'revenue_growth', 'revenue_change_percentage'])),
      positive: asNumber(pick(summary, ['revenue_trend', 'revenue_growth', 'revenue_change_percentage'], 0)) >= 0,
    },
    {
      label: 'ORDERS',
      value: formatNumber(orders),
      trend: formatTrend(pick(summary, ['orders_trend', 'orders_growth', 'orders_change_percentage'])),
      positive: asNumber(pick(summary, ['orders_trend', 'orders_growth', 'orders_change_percentage'], 0)) >= 0,
    },
    {
      label: 'AVG ORDER VALUE',
      value: formatMoney(avgOrder),
      trend: formatTrend(pick(summary, ['avg_order_value_trend', 'aov_growth', 'aov_change_percentage'])),
      positive: asNumber(pick(summary, ['avg_order_value_trend', 'aov_growth', 'aov_change_percentage'], 0)) >= 0,
    },
    {
      label: 'DISCOUNTS GIVEN',
      value: formatMoney(discount),
      meta: `${formatNumber(voucherCount)} vouchers • ${formatNumber(promoCount)} promo codes`,
    },
  ]

  const revenueRows = payload.revenue_chart || payload.daily_revenue || payload.chart || payload.sales_chart || []
  const revenueData = Array.isArray(revenueRows) && revenueRows.length
    ? revenueRows.map((row) => ({
      date: pick(row, ['date_label', 'label', 'date', 'day'], ''),
      revenue: asNumber(pick(row, ['revenue', 'sales', 'total', 'amount'], 0)),
    }))
    : DEFAULT_REVENUE_DATA

  const productRows = payload.top_selling || payload.top_selling_items || payload.top_products || []
  const topSelling = Array.isArray(productRows)
    ? productRows.map((item, index) => ({
      id: pick(item, ['id'], index + 1),
      rank: index + 1,
      name: pick(item, ['name', 'product_name', 'title'], 'Unnamed item'),
      category: pick(item, ['category', 'category_name'], 'Item'),
      price: formatMoney(pick(item, ['price', 'normal_price'], 0)),
      revenue: formatMoney(pick(item, ['revenue', 'total_revenue', 'sales', 'total_sales'], 0)),
      sold: `${formatNumber(pick(item, ['sold', 'quantity_sold', 'qty'], 0))} sold`,
      image: pick(item, ['image', 'image_url', 'thumbnail_image'], ''),
    }))
    : []

  const orderRows = payload.recent_orders || payload.orders || payload.latest_orders || []
  const recentOrders = Array.isArray(orderRows)
    ? orderRows.map((order) => {
      const items = order.items || order.order_items || []
      const itemText = Array.isArray(items) && items.length
        ? items.map((item) => `${pick(item, ['name', 'product_name'], 'Item')} x ${pick(item, ['quantity', 'qty'], 1)}`).join(', ')
        : pick(order, ['items', 'description', 'summary'], 'No items')

      return {
        id: pick(order, ['order_number', 'number', 'id'], '-'),
        items: itemText,
        customer: pick(order.customer || order, ['customer_name', 'name'], 'Guest'),
        status: pick(order, ['status'], 'Pending'),
        amount: formatMoney(pick(order, ['amount', 'grand_total', 'total', 'total_amount'], 0)),
      }
    })
    : []

  return {
    stats,
    revenueData,
    topSelling,
    orders: recentOrders,
    merchantName: pick(payload, ['merchant_name', 'outlet_name', 'store_name'], 'Cuscapi Merchant'),
    userName: pick(payload.user || payload, ['user_name', 'name'], 'Merchant'),
  }
}

const getDashboardParams = (dateFilter, customRange, selectedOutletId = null) => {
  const params = { period: dateFilter, outlet_id: selectedOutletId }

  if (dateFilter === 'custom' && customRange.start && customRange.end) {
    params.start_date = customRange.start.toISOString().slice(0, 10)
    params.end_date = customRange.end.toISOString().slice(0, 10)
  }

  return params
}

const DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'custom', label: 'Custom dates' },
]


const MAX_RANGE_DAYS = 92

function DateRangeDropdown({ value, customRange, onChange, onCustomRange }) {
  const [open, setOpen] = useState(false)
  const [showCal, setShowCal] = useState(false)
  const [leftMonth, setLeftMonth] = useState(() => {
    const d = new Date()
    d.setDate(1)
    return d
  })
  const [draft, setDraft] = useState({ start: null, end: null, hover: null })
  const ref = useRef(null)

  const isCustom = value === 'custom'
  const isActive = value !== 'today'
  const rightMonth = new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1)

  const triggerLabel = (() => {
    if (isCustom && customRange.start && customRange.end) {
      const fmt = (d) => d.toLocaleDateString('en-MY', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })

      return `${fmt(customRange.start)} – ${fmt(customRange.end)}`
    }

    return DATE_OPTIONS.find((o) => o.value === value)?.label ?? 'Date range'
  })()

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false)
        setShowCal(false)
      }
    }

    document.addEventListener('mousedown', handler)

    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const selectPreset = (option) => {
    if (option.value === 'custom') {
      setShowCal(true)
      setDraft({ start: null, end: null, hover: null })
      return
    }

    onChange(option.value)
    setOpen(false)
    setShowCal(false)
  }

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay()

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString()

  const isInRange = (day, start, end) => {
    if (!start || !end) return false

    const t = day.getTime()

    return t > Math.min(start.getTime(), end.getTime()) &&
      t < Math.max(start.getTime(), end.getTime())
  }

  const isHoverRange = (day, start, hover) => {
    if (!start || !hover || draft.end) return false

    const t = day.getTime()

    return t > Math.min(start.getTime(), hover.getTime()) &&
      t < Math.max(start.getTime(), hover.getTime())
  }

  const exceedsMax = (start, end) => {
    if (!start || !end) return false

    const diff = Math.abs(end - start) / (1000 * 60 * 60 * 24)

    return diff > MAX_RANGE_DAYS
  }

  const handleDayClick = (day) => {
    if (!draft.start || draft.end) {
      setDraft({ start: day, end: null, hover: null })
      return
    }

    if (exceedsMax(draft.start, day)) return

    const start = draft.start < day ? draft.start : day
    const end = draft.start < day ? day : draft.start

    setDraft({ start, end, hover: null })
  }

  const handleApply = () => {
    if (!draft.start || !draft.end) return

    onCustomRange({ start: draft.start, end: draft.end })
    onChange('custom')
    setOpen(false)
    setShowCal(false)
  }

  const prevMonth = () => {
    setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1))
  }

  const renderMonth = (baseDate) => {
    const year = baseDate.getFullYear()
    const month = baseDate.getMonth()
    const days = getDaysInMonth(year, month)
    const startDow = getFirstDayOfWeek(year, month)
    const monthLabel = baseDate.toLocaleDateString('en-MY', {
      month: 'long',
      year: 'numeric',
    })

    const cells = []

    for (let i = 0; i < startDow; i += 1) cells.push(null)
    for (let d = 1; d <= days; d += 1) cells.push(new Date(year, month, d))

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const maxEnd = draft.start && !draft.end
      ? new Date(draft.start.getTime() + MAX_RANGE_DAYS * 86400000)
      : null

    return (
      <div className="drcal__month">
        <div className="drcal__month-label">
          {monthLabel}
        </div>

        <div className="drcal__weekdays">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <span key={day} className="drcal__wd">
              {day}
            </span>
          ))}
        </div>

        <div className="drcal__grid">
          {cells.map((day, index) => {
            if (!day) return <span key={`empty-${index}`} />

            const isStart = isSameDay(day, draft.start)
            const isEnd = isSameDay(day, draft.end)
            const inRange = isInRange(day, draft.start, draft.end)
            const inHover = isHoverRange(day, draft.start, draft.hover)
            const isToday = isSameDay(day, today)
            const disabled = maxEnd ? day > maxEnd : false

            return (
              <button
                key={day.toISOString()}
                type="button"
                className={[
                  'drcal__day',
                  isStart ? 'drcal__day--start' : '',
                  isEnd ? 'drcal__day--end' : '',
                  inRange ? 'drcal__day--in-range' : '',
                  inHover ? 'drcal__day--hover-range' : '',
                  isToday ? 'drcal__day--today' : '',
                  disabled ? 'drcal__day--disabled' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => !disabled && handleDayClick(day)}
                onMouseEnter={() => {
                  if (draft.start && !draft.end) {
                    setDraft((prev) => ({ ...prev, hover: day }))
                  }
                }}
                disabled={disabled}
              >
                {day.getDate()}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`filter-drop ${open ? 'filter-drop--open' : ''} ${isActive ? 'filter-drop--active' : ''
        }`}
      ref={ref}
    >
      <button
        type="button"
        className="filter-drop__trigger"
        onClick={() => {
          setOpen((prev) => !prev)
          if (!open) setShowCal(isCustom)
        }}
      >
        <span className="filter-drop__label">
          Date range:
        </span>
        <span className="filter-drop__value">
          {triggerLabel}
        </span>
        <CaretDown size={14} className="filter-drop__caret" />
      </button>

      {open && !showCal && (
        <div className="filter-drop__menu">
          {DATE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`filter-drop__option ${value === option.value ? 'filter-drop__option--selected' : ''
                }`}
              onClick={() => selectPreset(option)}
            >
              {option.label}

              {value === option.value && (
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="filter-drop__check"
                />
              )}
            </button>
          ))}
        </div>
      )}

      {open && showCal && (
        <div className="drcal">
          <div className="drcal__header">
            <button type="button" className="drcal__nav" onClick={prevMonth}>
              <CaretDown size={14} style={{ transform: 'rotate(90deg)' }} />
            </button>

            <span />

            <button type="button" className="drcal__nav" onClick={nextMonth}>
              <CaretDown size={14} style={{ transform: 'rotate(-90deg)' }} />
            </button>
          </div>

          <div className="drcal__calendars">
            {renderMonth(leftMonth)}
            {renderMonth(rightMonth)}
          </div>

          <div className="drcal__footer">
            <span className="drcal__hint">
              {!draft.start
                ? 'Select start date'
                : !draft.end
                  ? 'Select end date (max 3 months)'
                  : `${draft.start.toLocaleDateString('en-MY', {
                    day: 'numeric',
                    month: 'short',
                  })} – ${draft.end.toLocaleDateString('en-MY', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}`
              }
            </span>

            <div className="drcal__footer-actions">
              <button
                type="button"
                className="drcal__back"
                onClick={() => {
                  setShowCal(false)
                  setDraft({ start: null, end: null, hover: null })
                }}
              >
                ← Back
              </button>

              <button
                type="button"
                className="drcal__apply"
                disabled={!draft.start || !draft.end}
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Dashboard() {
  const [dateFilter, setDateFilter] = useState('today')
  const [customRange, setCustomRange] = useState({
    start: null,
    end: null,
  })
  const [dashboard, setDashboard] = useState({
    stats: DEFAULT_STATS,
    revenueData: DEFAULT_REVENUE_DATA,
    topSelling: [],
    orders: [],
    merchantName: 'Cuscapi Merchant',
    userName: 'Merchant',
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [userInfo, setUserInfo] = useState(null)
  const [selectedOutletId, setSelectedOutletId] = useState(null)
  const navigate = useNavigate()

  const todayLabel = useMemo(() => new Date().toLocaleDateString('en-MY', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }), [])

  useEffect(() => {
    loadUserInfo()
  }, [])

  useEffect(() => {
    let active = true

    const loadDashboard = async () => {
      setLoading(true)
      setError('')

      try {
        const response = await merchantApi.dashboard.summary(getDashboardParams(dateFilter, customRange, selectedOutletId))
        if (active) {
          setDashboard(normalizeDashboard(response))
        }
      } catch (err) {
        if (active) {
          setError(err.message || 'Unable to load dashboard.')
          setDashboard((prev) => ({ ...prev, stats: DEFAULT_STATS, revenueData: DEFAULT_REVENUE_DATA }))
        }
      } finally {
        if (active) setLoading(false)
      }
    }
    if (selectedOutletId) {
      loadDashboard()
    }

    return () => {
      active = false
    }
  }, [dateFilter, customRange, selectedOutletId])

  const loadUserInfo = async () => {
    try {
      const userInfoResponse = await merchantApi.auth.me()
      setUserInfo(userInfoResponse?.data || null)
      setSelectedOutletId(userInfoResponse?.data?.default_outlet?.id || null)
    } catch (err) {
      console.error('Failed to load user info:', err)
    }
  }

  const { stats, revenueData, topSelling, orders, merchantName, userName } = dashboard

  return (
    <div className="dashboard-page">
      {/* =======================================================
                HEADER
            ======================================================= */}
      <Header
        items={[
          { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
          { label: 'Dashboard', current: true },
        ]}
      />
      <div className="dashboard-topbar">
        <div className="dashboard-heading">
          <div>
            <h1 className="dashboard-title">
              Good morning, {userInfo?.name || 'User'}
            </h1>

            <p className="dashboard-subtitle">
              {todayLabel} · {userInfo?.merchant?.business_name || 'Merchant'}
            </p>
          </div>
        </div>

        <div className="dashboard-filters">
          <DateRangeDropdown
            value={dateFilter}
            customRange={customRange}
            onChange={setDateFilter}
            onCustomRange={setCustomRange}
          />
        </div>
      </div>

      {error && (
        <div className="dashboard-alert">
          {error}
        </div>
      )}

      {loading && (
        <div className="dashboard-loading">
          Loading dashboard...
        </div>
      )}

      {/* =======================================================
                STATS
            ======================================================= */}

      <div className="dashboard-stats">
        {stats.map((item, index) => (
          <div
            className="dashboard-stat"
            key={index}
          >
            <div className="dashboard-stat-label">
              {item.label}
            </div>

            <div className="dashboard-stat-value">
              {item.value}
            </div>

            {item.trend && (
              <div
                className={`dashboard-stat-trend ${item.positive
                  ? 'dashboard-stat-trend--positive'
                  : 'dashboard-stat-trend--negative'
                  }`}
              >
                {item.trend}
              </div>
            )}

            {item.meta && (
              <div className="dashboard-stat-meta">
                {item.meta}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* =======================================================
                MAIN GRID
            ======================================================= */}

      <div className="dashboard-grid">
        {/* ===========================
                    CHART
                =========================== */}

        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <div className="dashboard-card-title">
              Daily revenue
            </div>
          </div>

          <div className="dashboard-chart">
            <ResponsiveContainer
              width="100%"
              height={320}
            >
              <AreaChart
                data={revenueData}
                margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0,
                }}
              >
                <defs>
                  <linearGradient
                    id="revenueFill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="#20B6D2"
                      stopOpacity={0.18}
                    />

                    <stop
                      offset="100%"
                      stopColor="#20B6D2"
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="4 4"
                  stroke="#E4E4E7"
                  vertical={true}
                />

                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{
                    fill: '#71717A',
                    fontSize: 12,
                  }}
                />

                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{
                    fill: '#71717A',
                    fontSize: 12,
                  }}
                />

                <Tooltip />

                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#20B6D2"
                  strokeWidth={3}
                  fill="url(#revenueFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ===========================
                    TOP SELLING
                =========================== */}

        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <div className="dashboard-card-title">
              Top-selling items
            </div>
          </div>

          <div className="dashboard-products">
            {!topSelling.length && (
              <div className="dashboard-empty">
                No top-selling items yet.
              </div>
            )}

            {topSelling.map((item) => (
              <div
                className="dashboard-product"
                key={item.id}
              >
                <div className="dashboard-product-rank">
                  {item.rank || item.id}
                </div>

                {item.image ? (
                  <img
                    className="dashboard-product-image"
                    src={item.image}
                    alt={item.name}
                  />
                ) : (
                  <div className="dashboard-product-image dashboard-product-image--empty" />
                )}

                <div className="dashboard-product-content">
                  <div className="dashboard-product-name">
                    {item.name}
                  </div>

                  <div className="dashboard-product-meta">
                    {item.category}
                    {/* • {item.price} */}
                  </div>
                </div>

                <div className="dashboard-product-sales">
                  <div className="dashboard-product-revenue">
                    {item.revenue}
                  </div>

                  <div className="dashboard-product-count">
                    {item.sold}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* =======================================================
                RECENT ORDERS
            ======================================================= */}

      <div className="dashboard-orders">
        <div className="dashboard-orders-header">
          <div className="dashboard-orders-title">
            Recent orders
          </div>
        </div>

        {!orders.length && (
          <div className="dashboard-empty">
            No recent orders yet.
          </div>
        )}

        {orders.map((item) => (
          <div
            className="dashboard-order"
            key={item.id}
          >
            <div className="dashboard-order-id">
              {item.id}
            </div>

            <div className="dashboard-order-content">
              <div className="dashboard-order-items">
                {item.items}
              </div>

              <div className="dashboard-order-meta">
                {item.customer} • {item.amount}
              </div>
            </div>
            <span className={`orders-table__status orders-table__status--${item.status}`}>
              <Icon icon="ph:circle-fill" height={6} /> {item.status ? item.status[0].toUpperCase() + item.status.slice(1) : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}